import React, { createContext, useContext, useState, useCallback, useLayoutEffect } from 'react';
import { lnAssets } from '../utils/lnAssets';

/** Site theme switcher: Bloom, Light, Dark Olive, Rose */
export type I3ThemeId = 'bloom' | 'light' | 'darkOlive' | 'rose';

const STORAGE_KEY = 'bridge_theme';
const VALID_THEMES: I3ThemeId[] = ['bloom', 'light', 'darkOlive', 'rose'];

type I3ThemeContextValue = {
  theme: I3ThemeId;
  setTheme: (theme: I3ThemeId) => void;
  logoUrl: string;
  accentColor: string;
};

const Context = createContext<I3ThemeContextValue | null>(null);

const logoByTheme: Record<I3ThemeId, string> = {
  bloom: lnAssets.logos.earth,
  light: lnAssets.logos.earth,
  darkOlive: lnAssets.logos.teal,
  rose: lnAssets.logos.earth,
};

/* Must match --i3-accent in LisaBrandOverrides / theme tokens */
const accentByTheme: Record<I3ThemeId, string> = {
  bloom: '#6f8f99',
  light: '#6f8f99',
  darkOlive: '#cbb79a',
  rose: '#4f6b73',
};

export const I3ThemeProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [theme, setThemeState] = useState<I3ThemeId>(() => {
    if (typeof window === 'undefined') return 'bloom';
    const stored = sessionStorage.getItem(STORAGE_KEY) as I3ThemeId | null;
    if (stored && VALID_THEMES.includes(stored)) return stored;
    return 'bloom';
  });

  useLayoutEffect(() => {
    sessionStorage.setItem(STORAGE_KEY, theme);
    document.documentElement.setAttribute('data-i3-theme', theme);
  }, [theme]);

  const setTheme = useCallback((next: I3ThemeId) => {
    setThemeState(next);
  }, []);

  const value: I3ThemeContextValue = {
    theme,
    setTheme,
    logoUrl: logoByTheme[theme],
    accentColor: accentByTheme[theme],
  };

  return <Context.Provider value={value}>{children}</Context.Provider>;
};

export function useI3Theme(): I3ThemeContextValue {
  const ctx = useContext(Context);
  if (!ctx) throw new Error('useI3Theme must be used inside I3ThemeProvider');
  return ctx;
}
